// !!!! - DO NOT MODIFY - !!!!
// THIS FILE IS DEPLOYED AUTOMATICALLY BY MATTv2.
//
//  Deployment info:
//    Updated at:     2026-06-08T12:07:23+00:00
//    Cause:          Manual deployment.
//    Deployment ID:  192397
//    Project:        628
//    Environment:    8046 - master
//    MD5:            930dc658e2fca03cddcb22e633de8f43
//    Signature:      eyJpdiI6Im5nNG1YdFJSRHBTRDkrVGx2RVZOXC9RPT0iLCJ2YWx1ZSI6InI5WTVmQ29WNkwzZmx5QTlNUEVYNTNXXC9uYjladEhITkFKZDdNakdLc2VRdnRDSnpucXVwMVJNMnZuMjI0akEwUjJsUThDWXdWeXZIZ2JaSXpHNUNRbDdiVGVmSTVmUlNieXBGSk1OcEFmWUJNbnErTFE4UWtGWkdvcSszYWRkd3RzTEJCQ1BIcnhFUGRPSktkaVk2WE9ySmtKMEtCV3ZtQUhcL09adFdOSm5CNnVndG12M2t2bE5PN0tsUVZiTkdZb2xqZVcrMnIyK1wvc0czY2owMFhlazBvQ1VKTXJJWU5QcW9NQ2UxSE5YWXp6ZkRCWUEwTXozUGZGOWRNMlVDekNJWEJLM28zNDlmSXJlTTgwd2hKRzBJSlpXSXA5WDhMWUhxT1hRaGV3djlsZXV4d3h0VURvRWFNWlYxU3EwNFRTV01uR2I2VUs2VExQVmpZNVNBK0NxZXhsN1k2QlE3ZlwvUWE2WUdSek5kSUNHSXVXRnpzbWg1SllVRStPaDdyd3g3aEtCa1dBM3pqQzlKTXF3aUo0SXBpeWpXRlVvMTlyd3ZUaERPOUo5RURRPSIsIm1hYyI6ImQyODMwOTcxOGU5MmJhZDRiMGJjNTYxOGMwNWVmMTMzZWQwZTFiMDJiMGY5YjVjNTllNjY3Njc0ZGIxZjBjOTEifQ==
// @ts-check 
import { calculateActualSizes, calculateFittedDpi, calculateRelativeRects } from './frame-sizing.js';

/**
 * @template T
 * @param {T} predicate 
 * @param {string} msg 
 * @returns {asserts predicate}
 */
const invariant = (predicate, msg = 'Invariant failed') => {
    if (!predicate) {
        throw new Error(msg);
    }
}

/**
 * @typedef {Object} UploadEventMeta
 * @property {number} contentLength
 * @property {number} height
 * @property {string} productHandle
 * @property {number} productId
 * @property {string} productTitle
 * @property {string} variant
 * @property {number} width
 */
/** 
 * @typedef {Object} UploadEventDetail
 * @property {UploadEventMeta} meta
 * @property {string} mimeType
 * @property {string} name
 * @property {string} path
 * @property {number} size
 * @property {string} token
 * @property {string} url
 * @property {string} uuid
 */
/**
 * @typedef {CustomEvent<UploadEventDetail>} UploadEvent
 */

/**
 * @typedef {Object} FrameContext
 * @property {number} maxDpi
 * @property {number} minDpi
 * @property {FrameContextMessages} messages
 * @property {FrameContextVariant[]} variants
 * @property {FrameContextImage|null} presetImage
 * 
 * @typedef {Object} FrameContextMessages
 * @property {string} frameTooLarge
 * @property {string} frameTooSmall
 * @property {string} showAtScaleEnable
 * @property {string} showAtScaleDisable
 * 
 * @typedef {Object} FrameContextVariant
 * @property {string} id
 * @property {string[]} options
 * @property {FrameContextImage} frameImage
 * @property {FrameContextFrameSizing} frameSizing
 * @property {FrameContextScalePreview} scalePreview
 * @property {string} mountDescription
 * 
 * @typedef {Object} FrameContextImage
 * @property {string} url
 * @property {number} width
 * @property {number} height
 * 
 * @typedef {Object} FrameContextFrameSizing
 * @property {[number, number]} outerSizeMillimetres
 * @property {[number, number, number, number]} outerRect
 * @property {[number, number, number, number]} innerRect
 * 
 * @typedef {Object} FrameContextScalePreview
 * @property {FrameContextImage} image
 * @property {number} widthMillimetres
 * @property {[number, number]} position
 */

export class FrameProductElement extends HTMLElement {
    /**
     * @typedef {Object} Refs
     * @property {HTMLElement} gallery
     * @property {import('./component-frame-preview').FramePreviewElement} preview
     * @property {HTMLScriptElement} context
     * @property {HTMLButtonElement} toggleScalePreview
     * @property {HTMLElement} variantSelector
     * @property {HTMLElement} sizeErrorMessage
     * @property {HTMLElement} uploadField
     * @property {HTMLElement} buyButtons
     * @property {HTMLElement} previewWrapper
     */
    /** @type {Refs|undefined} */
    #refs;
    /** @type {FrameContext|undefined} */
    #context;
    /** @type {UploadEventDetail|undefined} */
    #currentUploadDetail;
    /** @type {FrameContextVariant|undefined} */
    #currentVariantContext;
    #scalePreview = false;


    get landscape() {
        return !!this.#currentUploadDetail && this.#currentUploadDetail.meta.width > this.#currentUploadDetail.meta.height;
    }

    connectedCallback() {
        this.#collectRefs();
        invariant(this.#refs);

        // Parse context settings from liquid
        try {
            this.#context = JSON.parse(this.#refs.context.textContent);
        } catch (e) {
            console.error(`Failed to load context`, e);
            throw e;
        }
        invariant(this.#context);

        // Hide option selectors until upload
        this.#refs.variantSelector.style.display = 'none';
        this.#refs.buyButtons.style.display = 'none';

        // Handle file uploads
        // @ts-ignore
        this.addEventListener('upload:added', this.#handleUpload);
        // @ts-ignore
        this.addEventListener('upload:removed', this.#handleUploadRemoved);

        // Handle toggle scale preview
        this.#refs.toggleScalePreview.addEventListener('click', this.#handleToggleScalePreview);

        // Handle updating preview when selected variant changes
        this.addEventListener('change',(e) => {
            invariant(this.#context);
            if (e.target instanceof HTMLElement && e.target.matches('input[name=id],select[name=id]')) {
                this.#updateSelectedVariant();
                this.showPreview();
            }
        });


        // If a preset image is set, skip the upload stage
        if (this.#context.presetImage) {
            this.#handleUpload(new CustomEvent('upload:added', {
                detail: {
                    meta: {
                        contentLength: 0,
                        height: this.#context.presetImage.height,
                        productHandle: '',
                        productId: 0,
                        productTitle: '',
                        variant: '',
                        width: this.#context.presetImage.width
                    },
                    mimeType: '',
                    name: '',
                    path: '',
                    size: 0,
                    token: '',
                    url: this.#context.presetImage.url,
                    uuid: ''
                }
            }));
            this.#refs.uploadField.style.display = 'none';
            this.dataset.frameOrientation = this.landscape ? 'landscape' : 'portrait';
        }

        this.#setupMobileSticky();
    }

     /**
     * Toggle `product-single__mobile-sticky` on the sticky gallery column once
     * the page is scrolled, collapsing the preview into a sticky bar. The class
     * only has visual effect on mobile (see CSS media query).
     */
    #setupMobileSticky() {
        const sticky = this.querySelector('.product-single__sticky');
        if (!sticky) return;

        let reqFrame = false;
        let isSticky = false;
        const update = () => {
            reqFrame = false;
            const shouldStick = isSticky ? window.scrollY > 0 : window.scrollY > 8;
            if (shouldStick === isSticky) return;
            isSticky = shouldStick;
            sticky.classList.toggle('product-single__mobile-sticky', isSticky);
        };

        const onScroll = () => {
            if (reqFrame) return;
            reqFrame = true;
            requestAnimationFrame(update);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        update();
    }

    #collectRefs() {
        this.#refs = /** @type {Refs} */ ({
            gallery: this.querySelector('[data-frame-ref="gallery"]'),
            preview: this.querySelector('[data-frame-ref="preview"]'),
            context: this.querySelector('[data-frame-ref="context"]'),
            toggleScalePreview: this.querySelector('[data-frame-ref="toggleScalePreview"]'),
            variantSelector: this.querySelector('[data-frame-ref="variantSelector"]'),
            sizeErrorMessage: this.querySelector('[data-frame-ref="sizeErrorMessage"]'),
            uploadField: this.querySelector('[data-frame-ref="uploadField"]'),
            buyButtons: this.querySelector('[data-frame-ref="buyButtons"]'),
            previewWrapper: this.querySelector('[data-frame-ref="previewWrapper"]'),
        });
        if (!this.#refs?.gallery){
            throw new Error('Missing gallery ref');
        }
        if (!this.#refs?.preview){
            throw new Error('Missing preview ref');
        }
        if (!this.#refs?.context){
            throw new Error('Missing context ref');
        }
        if (!this.#refs?.toggleScalePreview){
            throw new Error('Missing toggleScalePreview ref');
        }
        if (!this.#refs?.variantSelector){
            throw new Error('Missing variantSelector ref');
        }
    }

    /** 
     * @param {UploadEvent} e 
     */
    #handleUpload = (e) => {
        invariant(this.#refs);
        console.log('frame-product: handling new upload event', e);

        this.#currentUploadDetail = e.detail;
        this.#scalePreview = false;

        this.#updateHiddenInputs();

        if (this.#updateSizeOptions()) {
            this.#resolveInitialVariant();
            this.#updateSelectedVariant();

            // Show preview if sizes are available
            this.showPreview();

            // Hide error message fallback when sizes are available
            this.#refs.variantSelector.style.display = '';
            this.#refs.buyButtons.style.display = '';
            this.#refs.sizeErrorMessage.style.display = 'none';
        } else {
            this.showGallery();

            // Show error message fallback when all sizes unsuitable
            this.#refs.variantSelector.style.display = 'none';
            this.#refs.buyButtons.style.display = 'none';
            this.#refs.sizeErrorMessage.style.display = '';
        }
    };

    #handleUploadRemoved = () => {
        invariant(this.#refs);

        this.#currentUploadDetail = undefined;

        this.#refs.variantSelector.style.display = 'none';
        this.#refs.buyButtons.style.display = 'none';
        this.#refs.sizeErrorMessage.style.display = 'none';

        this.showGallery();
    };

    #updateHiddenInputs() {
        invariant(this.#refs);

        // Find add-to-cart form
        const variantInput = this.querySelector('input[name=id],select[name=id]');
        invariant(variantInput && (variantInput instanceof HTMLInputElement || variantInput instanceof HTMLSelectElement), 'could not find variant input');
        const atcForm = variantInput.form;
        invariant(atcForm);

        /**
         * @param {string} name 
         * @param {string} value 
         */
        const updateField = (name, value) => {
            /** @type {HTMLInputElement} */
            let input = /** @type {HTMLInputElement} */(atcForm.querySelector(`input[name="${name}"]`));
            if (!input) {
                input = document.createElement('input');
                input.name = name;
                input.type = 'hidden';
                atcForm.append(input);
            }
            input.value = value;
        };

        // Add or update hidden line item properties
        updateField('properties[Orientation]', this.landscape ? 'Landscape' : 'Portrait');
        if (this.#context?.presetImage) {
            updateField('properties[_user_image]', this.#context.presetImage.url.replace(/^\/\//, 'https://'));
        }
    }

    #handleToggleScalePreview = () => {
        invariant(this.#refs);
        invariant(this.#context);

        this.#scalePreview = !this.#scalePreview;
        this.#refs.toggleScalePreview.textContent = this.#scalePreview 
            ? this.#context.messages.showAtScaleDisable 
            : this.#context.messages.showAtScaleEnable;
        
        if (document.startViewTransition) {
            document.startViewTransition({
                update: () => {
                    this.showPreview(false);
                },
                types: ['frame-toggling-scale-preview']
            });
        } else {
            this.showPreview(false);
        }
    };

    #resolveInitialVariant() {
        // Pick an appropriate size after upload. 
        // Priority:
        // - any size named 'Standard'
        // - first size which is not disabled

        // This is done by emulating a click on the option so that the usual variant selection logic
        // from the theme still occurs

        // 'Standard' size
        const sizeOptionInputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll(`input[data-index=option1]`)]);
        invariant(sizeOptionInputs.length > 0);

        const standardSizeInput = sizeOptionInputs.find((input) => input.value === 'Standard');
        if (standardSizeInput && !standardSizeInput.disabled) {
            invariant(standardSizeInput.type === 'radio', 'expected size inputs to be radios');
            standardSizeInput.click();
            return;
        }

        // First non-disabled size
        const firstEnabledInput = sizeOptionInputs.find((input) => !input.disabled);
        invariant(!!firstEnabledInput, 'cannot resolveInitialVariant when no sizes are enabled');
        invariant(firstEnabledInput.type === 'radio', 'expected size inputs to be radios');
        firstEnabledInput.click();
    }

    #updateSelectedVariant() {
        invariant(this.#context);

        const variantInput = this.querySelector('input[name=id],select[name=id]');
        invariant(variantInput && (variantInput instanceof HTMLInputElement || variantInput instanceof HTMLSelectElement), 'could not find variant input');
        const selectedVariantId = variantInput.value;
        const variantContext = this.#context.variants.find((v) => v.id.toString() === selectedVariantId);
        invariant(variantContext, 'could not find variant context');
        if (!variantContext) {
            throw new Error('Could not find variant context');
        }
        this.#currentVariantContext = variantContext;
    }

    #updateSizeOptions() {
        invariant(this.#context);
        invariant(this.#refs);
        invariant(this.#currentUploadDetail, 'cannot update size options without upload');

        const sizeOptionInputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll(`input[data-index=option1]`)]);
        invariant(sizeOptionInputs.length > 0);

        let anySizeAvailable = false;

        for (const input of sizeOptionInputs) {
            const inputWrapper = input.closest('.variant-input');
            const issuesText = inputWrapper?.querySelector('.frame-product-option-value__size-issues');
            invariant(inputWrapper && inputWrapper instanceof HTMLElement);
            invariant(issuesText && issuesText instanceof HTMLElement);

            /** @type {FrameContextVariant|undefined} */
            const variantContext = this.#context.variants.find((v) => v.options[0] === input.value);
            invariant(variantContext, 'failed to find variant context for input');

            const actualSizes = calculateActualSizes(variantContext);
            //const relativeRects = calculateRelativeRects(variantContext);

            /** @type {import('./frame-sizing.js').Vec2} */
            const userImageDimensions = [
                this.#currentUploadDetail.meta.width,
                this.#currentUploadDetail.meta.height,
            ];
            if (userImageDimensions[0] > userImageDimensions[1]) {
                // Mounting dimensions are flipped for landscape uploads
                userImageDimensions.reverse();
            }

            const effectiveDpi = calculateFittedDpi(actualSizes.innerRect, userImageDimensions, 'cover');
            input.dataset.frameCalculatedDpi = `${effectiveDpi}`;

            let issue = null;

            // Skip DPI checks if the image was preset
            if (!this.#context.presetImage) {
                if (effectiveDpi < this.#context.minDpi) {
                    issue = this.#context.messages.frameTooLarge;
                } else if (effectiveDpi > this.#context.maxDpi) {
                    issue = this.#context.messages.frameTooSmall;
                }
            }

            if (!issue) {
                anySizeAvailable = true;
                input.disabled = false;
                issuesText.style.display = 'none';
                issuesText.textContent = '';
            } else {
                input.disabled = true;
                issuesText.style.display = 'unset';
                issuesText.textContent = issue;
            }
        }

        return anySizeAvailable;
    }

    showGallery() {
        invariant(this.#refs);

        this.#refs.gallery.style.display = '';
        this.#refs.previewWrapper.style.display = 'none';
    }
    /**
     * 
     * @param {boolean} shouldTransition 
     */
    showPreview(shouldTransition = true) {
        const update = () => {
            invariant(this.#context);
            invariant(this.#refs);
            invariant(this.#currentVariantContext, 'cannot show preview without selected variant');
            invariant(this.#currentUploadDetail, 'cannot show preview without upload');

            this.dataset.frameOrientation = this.landscape ? 'landscape' : 'portrait';


            this.#refs.preview.update(
                {
                    variantContext: this.#currentVariantContext,
                    landscape: this.landscape,
                    userImage: {
                        url: this.#currentUploadDetail.url, 
                        width: this.#currentUploadDetail.meta.width,
                        height: this.#currentUploadDetail.meta.height,
                    }, 
                },
                this.#scalePreview
            );

            this.#refs.gallery.style.display = 'none';
            this.#refs.previewWrapper.style.display = 'block';
            this.#refs.preview.style.display = 'block';
        };
        
        if (shouldTransition && document.startViewTransition) {
            document.startViewTransition({
                update,
                types: ['frame-toggling-preview']
            });
        } else {
            update();
        }
    }
};
if (!customElements.get('frame-product')) {
    customElements.define('frame-product', FrameProductElement);
}
;